import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import type { WhatsAppSelectedPhoneNumber } from "@multi-chat/shared";
import { ChannelIcon } from "../../components/channel/ChannelIcon.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import { senderDisplayName } from "./campaign-message-presentation.js";
import { CampaignComboSelect, campaignCompactFieldTriggerClass } from "./CampaignComboSelect.js";
import { campaignLocaleAlignClass } from "./campaigns-presentation.js";

const CHANNELS = ["WHATSAPP"] as const;
const SENDER_SEARCH_THRESHOLD = 5;

const readOnlyFieldClass =
  "flex h-9 w-full min-w-0 items-center gap-2 rounded-lg border border-line/70 bg-surface-muted/60 px-3 text-[0.8125rem]";

function FormField({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId?: string;
}): React.JSX.Element {
  return (
    <div className="grid min-w-0 gap-1.5" data-testid={testId}>
      <span className="text-[0.7rem] font-semibold leading-none text-ink-muted">{label}</span>
      {children}
    </div>
  );
}

function WhatsAppMark(): React.JSX.Element {
  return (
    <span
      className="inline-grid size-6 shrink-0 place-items-center rounded-md bg-whatsapp text-white"
      aria-hidden="true"
    >
      <ChannelIcon platformType="WHATSAPP" size={13} />
    </span>
  );
}

function ConnectedDot({ label }: { label: string }): React.JSX.Element {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-success-muted px-2 py-px text-[0.6875rem] font-semibold text-success">
      <span className="size-1.5 rounded-full bg-success" aria-hidden="true" />
      {label}
    </span>
  );
}

function SenderIdentity({
  sender,
  uiDir,
}: {
  sender: WhatsAppSelectedPhoneNumber;
  uiDir: string;
}): React.JSX.Element {
  const name = senderDisplayName(sender);
  const showPhone = name !== sender.phoneNumber;
  return (
    <span className={cn("grid min-w-0 flex-1 gap-px", campaignLocaleAlignClass(uiDir))}>
      <span dir={uiDir} className="block w-full truncate text-[0.8125rem] font-semibold leading-tight text-ink">
        {name}
      </span>
      {showPhone ? (
        <span className="block w-full truncate text-[0.6875rem] leading-tight text-muted">
          <LtrValue className="tabular-nums">{sender.phoneNumber}</LtrValue>
        </span>
      ) : null}
    </span>
  );
}

/** Channel, sender, phone and connection as stacked full-width form fields. */
export function CampaignWhatsappSenderCard({
  senders,
  selectedEndpointId,
  canMutate,
  onSelectEndpoint,
}: {
  senders: readonly WhatsAppSelectedPhoneNumber[];
  selectedEndpointId: number | null;
  canMutate: boolean;
  onSelectEndpoint: (platformEndpointId: number | null) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const uiDir = i18n.dir();
  const selected =
    senders.find((phone) => phone.platformEndpointId === selectedEndpointId) ??
    (senders.length === 1 ? senders[0]! : null);
  const multiple = senders.length > 1;
  const connected = senders.length > 0;
  const connectionLabel = connected
    ? t("campaigns.content.connected")
    : t("campaigns.content.channelUnavailable");

  return (
    <div className="grid min-w-0 gap-4" data-testid="campaign-whatsapp-sender">
      <FormField label={t("campaigns.content.channel")}>
        <CampaignComboSelect
          items={CHANNELS}
          getKey={(item) => item}
          isSelected={() => true}
          onSelect={() => undefined}
          searchable={false}
          disabled={!canMutate}
          triggerClassName={campaignCompactFieldTriggerClass}
          ariaLabel={t("campaigns.content.channel")}
          testId="campaign-channel-select"
          menuTestId="campaign-channel-select-menu"
          renderTrigger={() => (
            <>
              <WhatsAppMark />
              <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold text-ink">
                {t("campaigns.content.channelWhatsApp")}
              </span>
              {connected ? (
                <ConnectedDot label={connectionLabel} />
              ) : (
                <span className="text-[0.75rem] font-medium text-muted">{connectionLabel}</span>
              )}
            </>
          )}
          renderOption={() => (
            <span className="flex min-w-0 items-center gap-2.5">
              <WhatsAppMark />
              <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold">
                {t("campaigns.content.channelWhatsApp")}
              </span>
              {connected ? <ConnectedDot label={connectionLabel} /> : null}
            </span>
          )}
        />
      </FormField>

      <FormField label={t("campaigns.message.sender")}>
        {multiple ? (
          <div data-testid="campaign-sender-select">
            <CampaignComboSelect
              items={senders}
              getKey={(phone) => String(phone.platformEndpointId)}
              isSelected={(phone) => phone.platformEndpointId === selectedEndpointId}
              onSelect={(phone) => onSelectEndpoint(phone.platformEndpointId)}
              searchable={senders.length > SENDER_SEARCH_THRESHOLD}
              filter={(items, query) => {
                const needle = query.trim().toLowerCase();
                return items.filter(
                  (phone) =>
                    senderDisplayName(phone).toLowerCase().includes(needle) ||
                    phone.phoneNumber.includes(needle),
                );
              }}
              searchPlaceholder={t("campaigns.content.searchSenders")}
              noMatchesLabel={t("campaigns.content.noSenderMatches")}
              disabled={!canMutate}
              triggerClassName={campaignCompactFieldTriggerClass}
              ariaLabel={t("campaigns.message.sender")}
              testId="campaign-sender-select-trigger"
              menuTestId="campaign-sender-select-menu"
              optionTestId={(phone) => `campaign-sender-option-${phone.platformEndpointId}`}
              renderTrigger={() =>
                selected ? (
                  <SenderIdentity sender={selected} uiDir={uiDir} />
                ) : (
                  <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-subtle">
                    {t("campaigns.message.chooseSender")}
                  </span>
                )
              }
              renderOption={(phone) => <SenderIdentity sender={phone} uiDir={uiDir} />}
            />
          </div>
        ) : selected ? (
          <div className={readOnlyFieldClass} data-testid="campaign-sender-fixed">
            <SenderIdentity sender={selected} uiDir={uiDir} />
            <Lock size={13} className="shrink-0 text-subtle" aria-hidden="true" />
          </div>
        ) : (
          <div className={cn(readOnlyFieldClass, "text-subtle")}>
            {t("campaigns.message.chooseSender")}
          </div>
        )}
      </FormField>

      {selected ? (
        <div className="grid min-w-0 gap-4 pt-1 sm:grid-cols-2">
          <FormField label={t("campaigns.content.senderPhone")}>
            <div className={readOnlyFieldClass}>
              <LtrValue className="tabular-nums font-medium text-ink">{selected.phoneNumber}</LtrValue>
            </div>
          </FormField>
          <FormField label={t("campaigns.content.connection")}>
            <div className={readOnlyFieldClass}>
              <span
                className="inline-flex items-center gap-2 font-semibold text-success"
                data-testid="campaign-sender-connection"
              >
                <span
                  className="size-2 rounded-full bg-success ring-4 ring-success/15"
                  aria-hidden="true"
                />
                {t("campaigns.message.senderConnected")}
              </span>
            </div>
          </FormField>
        </div>
      ) : null}
    </div>
  );
}
